import { BrowserRouter as Router, Link, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { ProfileMenu } from './components/profile-menu';
import { SyncProgressBar } from './components/sync-progress-bar';
import { AppSidebar } from './components/app-sidebar';
import { SyfoIcon } from './components/icons/syfo-icon';
import { SyfoText } from './components/icons/syfo-text';
import { SyfoSidebarTrigger } from './components/syfo-sidebar-trigger';
import { VaultRoutes } from './components/vault-routes';
import { AppErrorBoundary } from './components/error-boundaries';
import { UpdateBanner } from './components/update-banner';
import { SidebarInset, SidebarProvider } from './components/ui/sidebar';
import { useAppDispatch, useAppSelector } from './stores/hooks';
import { sessionExpired as sessionExpiredAction } from './stores/vault-slice';
import { useSyncPauseResume } from './hooks/use-sync-pause-resume';
import { useSyncPolling } from './hooks/use-sync-polling';
import { setSessionExpiredHandler } from './lib/api-fetch';
import './app.css';
import './components/app-header.css';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isSessionExpired = useAppSelector(state => state.vault.auth.sessionExpired);

  // Wire up api-fetch session expired handler
  useEffect(() => {
    setSessionExpiredHandler(() => dispatch(sessionExpiredAction()));
  }, [dispatch]);

  // Redirect to sign-in when session expires
  useEffect(() => {
    if (isSessionExpired) {
      navigate('/signin');
    }
  }, [isSessionExpired, navigate]);

  // Pause / resume sync machines when connectivity changes
  useSyncPauseResume(dispatch);

  // Single polling loop: 10 s active vault, 30 s others, 60 s when tab hidden
  useSyncPolling(dispatch, { foregroundInterval: 10_000, backgroundInterval: 30_000, hiddenTabInterval: 60_000 });

  // Check if we're on the home page or vaults page
  const isHomePage = location.pathname === '/';
  const isVaultsPage = location.pathname === '/vaults';
  const shouldHideSidebar = isHomePage || isVaultsPage;

  const handleNavigateHome = () => {
    navigate('/');
  };

  return (
    <AppErrorBoundary onNavigateHome={handleNavigateHome}>
              {/* Sync Progress Bar */}
        
      <SidebarProvider>


        {/* Main Content Area */}
        <SidebarInset>
          {/* Update Banner - shown when a new version is available */}
          <UpdateBanner />

          {/* Global App Header - hidden on home page only */}
          {!isHomePage && (
            <header className="app-header-border sticky top-0 z-50 grid grid-cols-3 items-center py-2 px-4">
              <div className="flex items-center">
                <ProfileMenu />
              </div>
              <div className="flex items-center justify-center">
                <Link className="flex items-center" to="/">
                  <SyfoIcon className="h-10 w-10" />
                  <SyfoText className="h-10 w-auto" />
                </Link>
              </div>
              <div className="flex items-center justify-end gap-2">
                {!shouldHideSidebar && <SyfoSidebarTrigger />}
              </div>
            </header>
          )}

          {/* Page Content */}
          <div className="flex-1 overflow-auto">
            <Suspense fallback={
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              </div>
            }>
              <VaultRoutes />
            </Suspense>
          </div>
        </SidebarInset>
        
        {/* Sidebar - only show when not on home or vaults pages */}
        {!shouldHideSidebar && <AppSidebar />}
      </SidebarProvider>
      {/* Sync Progress Bar - always show (manages its own visibility) */}
      <SyncProgressBar />
    </AppErrorBoundary>
  );
}

// Wrapper component to provide Router context
function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;